import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import api from '../../services/api';

export default function EmployeeEditDialog({ employee, visible, onHide, onSave }) {
  const [form, setForm] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [roles] = useState(['employee', 'manager', 'admin']);
  const [managers, setManagers] = useState([]);
  const [approvalHierarchies, setApprovalHierarchies] = useState([]);
  const [expApprovalHierarchies, setExpApprovalHierarchies] = useState([]);
  const [saving, setSaving] = useState(false);

  const defaultDepartmentPosition = {
    department_id: null,
    position_id: null,
    start_date: null,
    end_date: null,
    is_current: true,
  };

  useEffect(() => {
    const fetchLists = async () => {
      const [deptRes, posRes, mgrRes, apprRes, expApprRes] = await Promise.all([
        api.get('/departments'),
        api.get('/positions'),
        api.get('/users'),
        api.get('/approval-hierarchies'),
        api.get('/expense-approval-hierarchies'),
      ]);
      setDepartments(deptRes.data);
      setPositions(posRes.data);
      setManagers(mgrRes.data);
      setApprovalHierarchies(apprRes.data);
      setExpApprovalHierarchies(expApprRes.data);
    };
    fetchLists();
  }, []);

  const initializeForm = (employeeData) => {
    if (!employeeData) return;

    const departmentPositions = (employeeData.departments_with_positions || []).map(dp => ({
      department_id: dp.department_id || null,
      position_id: dp.position_id || null,
      start_date: dp.start_date ? new Date(dp.start_date) : null,
      end_date: dp.end_date ? new Date(dp.end_date) : null,
      is_current: !dp.end_date,
    }));

    setForm({
      first_name: employeeData.first_name || '',
      last_name: employeeData.last_name || '',
      role: employeeData.role || '',
      phone_number: employeeData.phone_number || '',
      id_number: employeeData.id_number || '',
      birth_date: employeeData.birth_date ? new Date(employeeData.birth_date) : null,
      start_date: employeeData.start_date ? new Date(employeeData.start_date) : null,
      manager_id: employeeData.manager_id || null,
      approval_hierarchy: employeeData.approval_hierarchy?.name || '',
      expense_approval_hierarchy: employeeData.expense_approval_hierarchy?.name || '',
      department_positions: departmentPositions,
    });
  };

  useEffect(() => {
    if (!employee) {
      setForm(null);
      return;
    }

    initializeForm(employee);
  }, [employee]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleDeptPosChange = (index, field, value) => {
    const updated = [...form.department_positions];
    const current = { ...updated[index] };

    if (field === 'is_current') {
      current.is_current = value;
      if (value) {
        current.end_date = null;
      }
    } else {
      current[field] = value;
    }

    if (field === 'end_date' && value === null) {
      current.end_date = null;
    }

    updated[index] = current;
    setForm(prev => ({ ...prev, department_positions: updated }));
  };

  const formatDateForPayload = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    return value;
  };

  const buildDepartmentPositionsPayload = (departmentPositions = []) =>
    departmentPositions
      .filter(dp => dp.department_id && dp.position_id)
      .map(dp => ({
        department_id: dp.department_id,
        position_id: dp.position_id,
        start_date: formatDateForPayload(dp.start_date),
        end_date: dp.is_current ? null : formatDateForPayload(dp.end_date),
      }));

  const buildPayloadFromForm = (formState) => {
    const selectedAppr = approvalHierarchies.find(h => h.name === formState.approval_hierarchy);
    const selectedExpAppr = expApprovalHierarchies.find(h => h.name === formState.expense_approval_hierarchy);

    const payload = {
      ...formState,
      department_positions: buildDepartmentPositionsPayload(formState.department_positions),
      approval_hierarchy_id: selectedAppr?.id || null,
      expense_approval_hier_id: selectedExpAppr?.id || null,
      birth_date: formatDateForPayload(formState.birth_date),
      start_date: formatDateForPayload(formState.start_date),
    };

    delete payload.approval_hierarchy;
    delete payload.expense_approval_hierarchy;

    return payload;
  };

  const submitFormState = async (formState, { closeDialog = false } = {}) => {
    if (!employee?.id || !formState) return;

    setSaving(true);
    try {
      const payload = buildPayloadFromForm(formState);
      await api.put(`/users/${employee.id}`, payload);

      if (onSave) {
        await onSave();
      }

      if (closeDialog) {
        onHide?.();
      }

      return payload;
    } catch (error) {
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const addDeptPos = () => {
    setForm(prev => ({
      ...prev,
      department_positions: [
        ...(prev.department_positions || []),
        { ...defaultDepartmentPosition },
      ]
    }));
  };

  const removeDeptPos = async (index) => {
    if (!form) return;

    const previousForm = form;
    const updatedPositions = form.department_positions.filter((_, i) => i !== index);
    const nextFormState = {
      ...form,
      department_positions: updatedPositions,
    };

    setForm(nextFormState);

    try {
      await submitFormState(nextFormState);
    } catch (error) {
      setForm(previousForm);
    }
  };

  const handleSubmit = async () => {
    if (!form) return;
    try {
      await submitFormState(form, { closeDialog: true });
    } catch (error) {
      // Hata bildirimleri global interceptor üzerinden gösteriliyor.
    }
  };

  if (!form) return null;

  return (
    <Dialog
      header="Çalışan Düzenle"
      visible={visible}
      style={{ width: '900px', maxWidth: '98vw' }}
      modal
      onHide={onHide}
    >
      <div className="p-fluid grid">
        <div className="col-6">
          <label>Ad</label>
          <InputText value={form.first_name} onChange={(e) => handleChange('first_name', e.target.value)} />
        </div>
        <div className="col-6">
          <label>Soyad</label>
          <InputText value={form.last_name} onChange={(e) => handleChange('last_name', e.target.value)} />
        </div>
        <div className="col-6">
          <label>Rol</label>
          <Dropdown value={form.role} options={roles} onChange={(e) => handleChange('role', e.value)} />
        </div>
        <div className="col-6">
          <label>Telefon</label>
          <InputText value={form.phone_number} onChange={(e) => handleChange('phone_number', e.target.value)} />
        </div>
        <div className="col-6">
          <label>TC No</label>
          <InputText value={form.id_number} onChange={(e) => handleChange('id_number', e.target.value)} />
        </div>
        <div className="col-6">
          <label>Doğum Tarihi</label>
          <Calendar value={form.birth_date} onChange={(e) => handleChange('birth_date', e.value)} showIcon dateFormat="yy-mm-dd" />
        </div>
        <div className="col-6">
          <label>İşe Başlama</label>
          <Calendar value={form.start_date} onChange={(e) => handleChange('start_date', e.value)} showIcon dateFormat="yy-mm-dd" />
        </div>
        <div className="col-6">
          <label>Yönetici</label>
          <Dropdown
            value={form.manager_id}
            options={managers.map(m => ({ label: `${m.first_name} ${m.last_name}`, value: m.id }))}
            onChange={(e) => handleChange('manager_id', e.value)}
            placeholder="Yönetici seç"
          />
        </div>
        <div className="col-6">
          <label>İzin Onay Süreci</label>
          <Dropdown
            value={form.approval_hierarchy}
            options={approvalHierarchies.map(h => ({ label: h.name, value: h.name }))}
            onChange={(e) => handleChange('approval_hierarchy', e.value)}
            placeholder="Seçiniz"
          />
        </div>
        <div className="col-6">
          <label>Harcama Onay Süreci</label>
          <Dropdown
            value={form.expense_approval_hierarchy}
            options={expApprovalHierarchies.map(h => ({ label: h.name, value: h.name }))}
            onChange={(e) => handleChange('expense_approval_hierarchy', e.value)}
            placeholder="Seçiniz"
          />
        </div>


        <div className="col-12 mt-3">
          <h4>Departman & Pozisyonlar</h4>
          {form.department_positions.length === 0 && (
            <p className="text-sm text-500 mb-3">
              Bu çalışanın aktif bir departman kaydı bulunmuyor.
            </p>
          )}
          {form.department_positions.map((dp, index) => (
            <div
              key={index}
              className="flex flex-wrap gap-3 align-items-end mb-3"
            >
              <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
                <Dropdown
                  value={dp.department_id}
                  options={departments.map(d => ({ label: d.name, value: d.id }))}
                  onChange={(e) => handleDeptPosChange(index, 'department_id', e.value)}
                  placeholder="Departman"
                  className="w-full"
                />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
                <Dropdown
                  value={dp.position_id}
                  options={positions.map(p => ({ label: p.name, value: p.id }))}
                  onChange={(e) => handleDeptPosChange(index, 'position_id', e.value)}
                  placeholder="Pozisyon"
                  className="w-full"
                />
              </div>
              <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                <Calendar
                  value={dp.start_date}
                  onChange={(e) => handleDeptPosChange(index, 'start_date', e.value)}
                  showIcon
                  dateFormat="yy-mm-dd"
                  placeholder="Başlangıç Tarihi"
                  className="w-full"
                  inputClassName="w-full"
                />
              </div>
              {!dp.is_current && (
                <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                  <Calendar
                    value={dp.end_date}
                    onChange={(e) => handleDeptPosChange(index, 'end_date', e.value)}
                    showIcon
                    dateFormat="yy-mm-dd"
                    placeholder="Bitiş Tarihi"
                    className="w-full"
                    inputClassName="w-full"
                  />
                </div>
              )}
              <div
                className="flex align-items-center gap-2"
                style={{ flex: '1 1 160px', minWidth: '150px' }}
              >
                <Checkbox
                  inputId={`dept-pos-current-${index}`}
                  checked={dp.is_current}
                  onChange={(e) => handleDeptPosChange(index, 'is_current', e.checked)}
                />
                <label htmlFor={`dept-pos-current-${index}`} className="m-0">
                  Halen
                </label>
              </div>
              <div
                className="flex justify-content-end"
                style={{ flex: '1 1 140px', minWidth: '120px' }}
              >
                <Button
                  type="button"
                  label="Sil"
                  icon="pi pi-trash"
                  className="p-button-danger p-button-sm w-full md:w-auto"
                  onClick={() => removeDeptPos(index)}
                  disabled={saving}
                />
              </div>
            </div>
          ))}
          <Button
            label="Yeni Ekle"
            icon="pi pi-plus"
            className="p-button-sm mt-2 w-full md:w-auto"
            onClick={addDeptPos}
            disabled={saving}
          />
        </div>

        <div className="col-12 mt-4 flex justify-content-end">
          <Button
            label="Kaydet"
            icon="pi pi-check"
            onClick={handleSubmit}
            loading={saving}
            disabled={saving}
          />
        </div>
      </div>
    </Dialog>
  );
}
